import React from 'react';
import { useUser } from '../contexts/UserContext';
import './studioTheme.css';

const SunIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="4.2" />
    <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.5 1.5M17.6 17.6l1.5 1.5M19.1 4.9l-1.5 1.5M6.4 17.6l-1.5 1.5" />
  </svg>
);

const MoonIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 13.2A8.5 8.5 0 1 1 10.8 3a6.8 6.8 0 0 0 10.2 10.2z" />
  </svg>
);

/**
 * StudioThemeSwitch — light / dark, mounted once for the whole app.
 *
 * Reads and writes `studioTheme` on UserContext, so the choice is global:
 * switching here changes every page, and it persists across reloads.
 *
 * This is a true toggle — one control with a sliding thumb — rather than the
 * pair of radios it used to be. Both icons stay visible on the track so the
 * destination is readable at a glance; the thumb sits over whichever theme is
 * currently on.
 *
 * `role="switch"` with `aria-checked` reflecting "is light" is the accurate
 * mapping for a two-state control, and it responds to Space/Enter for free
 * because it is a real <button>.
 */
const StudioThemeSwitch = ({ className = '' }) => {
  const { studioTheme, setStudioTheme } = useUser();
  const isLight = studioTheme === 'light';

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isLight}
      aria-label={isLight ? 'Switch to dark theme' : 'Switch to light theme'}
      title={isLight ? 'Switch to dark theme' : 'Switch to light theme'}
      className={`cls-theme-switch ${isLight ? 'is-light' : 'is-dark'} ${className}`}
      onClick={() => setStudioTheme(isLight ? 'dark' : 'light')}
    >
      {/* Painted behind the icons and translated by the `is-*` class. */}
      <span className="cls-theme-thumb" aria-hidden="true" />
      <span className="cls-theme-ico cls-theme-ico--sun" aria-hidden="true"><SunIcon /></span>
      <span className="cls-theme-ico cls-theme-ico--moon" aria-hidden="true"><MoonIcon /></span>
    </button>
  );
};

export default StudioThemeSwitch;
